import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, AlertTriangle } from "lucide-react";
import { useSSA } from "./SSAContext";

export const Card = ({ title, eyebrow, action, children, className = "", testid }) => (
  <section className={`bg-white rounded-xl border border-slate-200 ${className}`} data-testid={testid}>
    {(title || action) && (
      <div className="flex items-start justify-between gap-3 px-5 pt-4">
        <div>
          {eyebrow && <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">{eyebrow}</div>}
          {title && <h3 className="font-display font-semibold text-slate-900">{title}</h3>}
        </div>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const DemoStamp = ({ text = "DEMO / SYNTHETIC DATA – NOT A REAL OFFER" }) => (
  <span className="inline-flex items-center gap-1 rounded border border-amber-400 bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" data-testid="demo-stamp">
    <AlertTriangle size={11} /> {text}
  </span>
);

const READY_TONE = {
  ready: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ready_with_assumptions: "bg-sky-50 text-sky-800 border-sky-200",
  discovery_required: "bg-violet-50 text-violet-800 border-violet-200",
  incomplete: "bg-rose-50 text-rose-800 border-rose-200",
};

export function ReadinessBadge({ value }) {
  const { config } = useSSA();
  if (!value) return null;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${READY_TONE[value]}`} data-testid="readiness-badge">{config?.readiness?.[value] || value}</span>;
}

export function StatusBadge({ status }) {
  const { config } = useSSA();
  const done = ["infosys_po_issued", "completed"].includes(status);
  const bad = ["declined", "cancelled", "returned_to_buyer"].includes(status);
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${done ? "bg-emerald-100 text-emerald-800" : bad ? "bg-rose-100 text-rose-800" : "bg-[#0B2A4A]/10 text-[#0B2A4A]"}`} data-testid="status-badge">
      {config?.statuses?.[status] || status}
    </span>
  );
}

const FLOW = ["submitted", "procurement_review", "rfq_issued", "responses_received", "evaluation", "shortlisted", "quote_issued", "buyer_accepted", "buyer_po_received", "infosys_po_issued"];
const SHORT = ["Submitted", "Review", "RFQ", "Responses", "Evaluation", "Shortlist", "Quote", "Accepted", "Buyer PO", "Infosys PO"];

export function StatusStepper({ status }) {
  const alias = { partner_clarification: "rfq_issued", buyer_clarification: "quote_issued" };
  const idx = status === "infosys_po_issued" ? FLOW.length : FLOW.indexOf(alias[status] || status);
  return (
    <ol className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="status-stepper">
      {SHORT.map((s, i) => (
        <li key={s} className="flex items-center gap-1 shrink-0">
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${i < idx ? "text-emerald-700" : i === idx ? "bg-[#0B2A4A] text-white" : "text-slate-400"}`}>
            {i < idx ? <CheckCircle2 size={12} /> : <Circle size={10} />} {s}
          </span>
          {i < SHORT.length - 1 && <span className={`w-4 h-px ${i < idx ? "bg-emerald-400" : "bg-slate-200"}`} />}
        </li>
      ))}
    </ol>
  );
}

export function AuditTimeline({ rid, perspective }) {
  const { call, refreshKey } = useSSA();
  const [events, setEvents] = useState([]);
  useEffect(() => { call("get", `/requests/${rid}/audit`, null, perspective).then((d) => setEvents(d.events)).catch(() => {}); }, [rid, perspective, call, refreshKey]);
  return (
    <ol className="relative border-l border-slate-200 ml-2 space-y-3" data-testid="audit-timeline">
      {events.map((e, i) => (
        <li key={i} className="ml-4">
          <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-[#0072CE]" />
          <div className="text-sm font-semibold text-slate-900">{e.action.replaceAll("_", " ")}</div>
          <div className="text-xs text-slate-500">
            {new Date(e.at).toLocaleString()} · user <span className="font-mono">{e.actor_user}</span> · view <span className="font-semibold">{e.perspective}</span>
            {e.to_status && <> · → {e.to_status}</>}
          </div>
          {e.detail && <div className="text-xs text-slate-600 mt-0.5">{e.detail}</div>}
        </li>
      ))}
      {!events.length && <li className="ml-4 text-sm text-slate-400">No events yet</li>}
    </ol>
  );
}

export const List = ({ items, tone = "slate" }) => (
  <ul className="space-y-1">
    {(items || []).map((x, i) => (
      <li key={i} className={`text-sm text-${tone}-700 flex gap-2`}><span className="text-slate-300">•</span><span>{typeof x === "string" ? x : JSON.stringify(x)}</span></li>
    ))}
  </ul>
);

export const Empty = ({ children }) => <div className="text-center py-14 text-slate-400 text-sm" data-testid="empty-state">{children}</div>;
